import { Fragment, useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../api/client'
import { type AktuellerZustand, letzterZustandHolen } from '../api/strecke'
import { useAuth } from '../auth/AuthContext'
import { Karte } from '../komponenten/KarteLazy'
import { oeffneKarte } from '../komponenten/karte-oeffnen'
import { FELD, telemetriefelder, wertText } from '../komponenten/telemetrie'

/**
 * Die Akte eines Fahrzeugs — alles, was über einen Bus bekannt ist, auf einer Seite.
 *
 * <h2>Aufbau</h2>
 *
 * Links die Stammdaten, die sich selten ändern; rechts der zuletzt gemeldete Zustand mit
 * der Karte. Diese Aufteilung folgt der Frage, mit der jemand herkommt: Die Werkstatt
 * sucht die Fahrgestellnummer oder den Prüftermin, die Disposition will wissen, wo der Bus
 * gerade ist. Beides ist einen Blick wert, keins muss man suchen.
 *
 * <h2>Solange keine Telematik angeschlossen ist</h2>
 *
 * Der rechte Teil bleibt leer, und das soll er auch zeigen: Ein leeres Feld ohne Erklärung
 * liest sich wie ein Fehler. Stattdessen steht dort, warum nichts da ist und wie ein Wert
 * hineinkommt — von Hand, bis ein Gerät verbaut ist.
 */

interface Zustand {
  id: number
  gemessenAm: string
  empfangenAm: string
  breite: number | null
  laenge: number | null
  richtung: number | null
  geschwindigkeit: number | null
  kmStand: number | null
  betriebsstunden: number | null
  fuellstand: number | null
  ladestand: number | null
  batterieSoc: number | null
  antriebsart: string | null
  zuendungAn: boolean | null
  fehlertext: string | null
  quelle: string
}

interface Akte {
  id: number
  kennzeichen: string
  fin: string | null
  interneNummer: string | null
  fahrzeuggruppe: string | null
  hersteller: string | null
  typ: string | null
  baujahr: number | null
  erstzulassung: string | null
  antrieb: string | null
  sitzplaetze: number | null
  stehplaetze: number | null
  laengeMeter: number | null
  betriebshofId: number | null
  betriebshofName: string | null
  huFaellig: string | null
  spFaellig: string | null
  serviceFaellig: string | null
  serviceKm: number | null
  telematikId: string | null
  bemerkung: string | null
  aktiv: boolean
  zustand: Zustand | null
}

const QUELLE_TEXT: Record<string, string> = {
  FMS: 'FMS-Gerät',
  HERSTELLER: 'Herstellerportal',
  APP: 'Fahrer-App',
  MANUELL: 'Handeintrag',
  TELEMATIK_LIVE: 'Telematik (live)',
}

// Antriebsart: die Stammdaten führen sie groß (Enum DIESEL/ELEKTRO/…), das Gerät erkennt sie
// selbst und sendet sie klein (elektro/diesel/hybrid). Beide Schreibweisen hier abgedeckt.
const ANTRIEB_WAHL: { wert: string; text: string }[] = [
  { wert: '', text: '– unbekannt –' },
  { wert: 'DIESEL', text: 'Diesel' },
  { wert: 'ERDGAS', text: 'Erdgas' },
  { wert: 'HYBRID', text: 'Hybrid' },
  { wert: 'ELEKTRO', text: 'Elektro' },
  { wert: 'WASSERSTOFF', text: 'Wasserstoff' },
]

const ANTRIEB_TEXT: Record<string, string> = {
  diesel: 'Diesel',
  erdgas: 'Erdgas',
  hybrid: 'Hybrid',
  elektro: 'Elektro',
  wasserstoff: 'Wasserstoff',
}

/** Elektro- oder Hybridantrieb? Trägt die E-Bus-Ansicht (Batterie statt Tank). */
function istElektroAntrieb(a: string | null | undefined): boolean {
  const s = (a ?? '').toLowerCase()
  return s.includes('elektro') || s.includes('hybrid')
}

function antriebText(a: string | null | undefined): string | null {
  if (!a) return null
  return ANTRIEB_TEXT[a.toLowerCase()] ?? a
}

/**
 * Baut aus dem Live-Zustand der Backend-API einen `Zustand`, wie ihn die Box erwartet. Die
 * Feldnamen der Nutzlast folgen denen des Geräts; was fehlt, bleibt null und wird in der Box
 * schlicht nicht gezeigt. So erscheint, was das Gerät wirklich sendet, ohne Vorannahme.
 */
function liveAlsZustand(l: AktuellerZustand): Zustand | null {
  const i = l.inhalt
  if (!i) return null
  const zahl = (v: unknown): number | null =>
    typeof v === 'number' && Number.isFinite(v) ? v : null
  const bool = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null)
  const text = (v: unknown): string | null => (typeof v === 'string' && v ? v : null)
  return {
    id: -1,
    gemessenAm: l.gemessenAm ?? l.empfangenAm ?? '',
    empfangenAm: l.empfangenAm ?? '',
    breite: zahl(i.breite),
    laenge: zahl(i.laenge),
    richtung: zahl(i.richtung),
    geschwindigkeit: zahl(i.geschwindigkeit),
    kmStand: zahl(i.kmStand),
    betriebsstunden: zahl(i.betriebsstunden),
    fuellstand: zahl(i.fuellstand),
    ladestand: zahl(i.ladestand),
    batterieSoc: zahl(i.batterieSoc),
    antriebsart: text(i.antriebsart),
    zuendungAn: bool(i.zuendungAn),
    fehlertext: text(i.fehlertext),
    quelle: 'TELEMATIK_LIVE',
  }
}

/** „vor 3 Min" usw. aus dem Alter; bei stillem Gerät ein Zusatz, damit man es nicht verwechselt. */
function alterText(l: AktuellerZustand): string {
  const s = l.alterSekunden
  let alter = ''
  if (s != null) {
    if (s < 90) alter = 'gerade gemeldet'
    else if (s < 5400) alter = `vor ${Math.round(s / 60)} Min`
    else if (s < 172800) alter = `vor ${Math.round(s / 3600)} Std`
    else alter = `vor ${Math.round(s / 86400)} Tagen`
  }
  if (l.aktuell === false) {
    return alter ? `${alter} · Gerät meldet gerade nicht` : 'Gerät meldet gerade nicht'
  }
  return alter
}

/**
 * Was NICHT in die label-getriebene Werteliste des Live-Zustands gehört: die Position steht auf
 * der Mini-Karte, der Fehler in seinem eigenen Hinweis, der Zeitpunkt in der Kopfzeile — und die
 * Backend-Nutzlast trägt Gerätenummer/Mandant/Zeiten doppelt, die hier nur Rauschen wären.
 */
const AUSSCHLUSS_BOX = new Set([
  'breite',
  'laenge',
  'ort',
  'richtung',
  'fehlertext',
  'gemessenAm',
  'empfangenAm',
  'geraetenummer',
  'mandant',
])

function datum(wert: string | null): string {
  return wert ? new Date(wert).toLocaleDateString('de-DE') : '–'
}

function zeitpunkt(wert: string): string {
  return new Date(wert).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Ein Prüftermin mit Ampel: überfällig rot, bald fällig orange. */
function Frist({ datum: wert }: { datum: string | null }) {
  if (!wert) return <span className="unbesetzt">–</span>
  const tage = Math.round((new Date(wert).getTime() - Date.now()) / 86400000)
  const farbe = tage < 0 ? 'var(--rot-500)' : tage < 30 ? 'var(--orange-600)' : undefined
  return (
    <span style={{ color: farbe, fontWeight: tage < 30 ? 600 : undefined }}>
      {new Date(wert).toLocaleDateString('de-DE')}
      {tage < 0 && ' (überfällig)'}
      {tage >= 0 && tage < 30 && ` (in ${tage} Tagen)`}
    </span>
  )
}

function Zeile({ was, children }: { was: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '150px 1fr',
        gap: 12,
        padding: '6px 0',
        borderBottom: '1px solid var(--grau-100)',
      }}
    >
      <span style={{ color: 'var(--grau-500)', fontSize: '0.85rem' }}>{was}</span>
      <span style={{ fontSize: '0.9rem' }}>{children}</span>
    </div>
  )
}

/** Ein Messwert mit Einheit, groß gesetzt — für den Blick aus zwei Metern Entfernung. */
function Wert({
  was,
  wert,
  einheit,
}: {
  was: string
  wert: number | string | null | undefined
  einheit?: string
}) {
  return (
    <div>
      <div style={{ color: 'var(--grau-500)', fontSize: '0.78rem' }}>{was}</div>
      <div style={{ fontSize: '1.15rem', fontWeight: 600 }}>
        {wert == null || wert === '' ? (
          <span className="unbesetzt" style={{ fontWeight: 400 }}>
            –
          </span>
        ) : (
          <>
            {typeof wert === 'number' ? wert.toLocaleString('de-DE') : wert}
            {einheit && (
              <span style={{ fontSize: '0.8rem', fontWeight: 400, marginLeft: 3 }}>{einheit}</span>
            )}
          </>
        )}
      </div>
    </div>
  )
}

/**
 * Der Kopf der Zustand-Karte macht den Unterschied E-Bus/Diesel sichtbar: ein Abzeichen mit der
 * (vom Gerät erkannten oder im Stamm gepflegten) Antriebsart und die passende große Energiekachel —
 * Batterie-Ladung beim Elektrobus, Kraftstoff beim Diesel.
 */
function ZustandKopf({
  istElektro,
  antrieb,
  batterieSoc,
  ladestand,
  fuellstand,
}: {
  istElektro: boolean
  antrieb: string | null
  batterieSoc: number | null
  ladestand: number | null
  fuellstand: number | null
}) {
  const energieWert = istElektro ? (batterieSoc ?? ladestand) : fuellstand
  const energieLabel = istElektro ? 'Batterie-Ladung' : 'Kraftstoff'
  const farbe = istElektro ? '#16a34a' : '#6b7280'
  if (!antrieb && energieWert == null) return null
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        marginBottom: 14,
        paddingBottom: 12,
        borderBottom: '1px solid var(--grau-100)',
      }}
    >
      {antrieb && (
        <span
          style={{
            display: 'inline-block',
            padding: '2px 10px',
            borderRadius: 12,
            background: farbe,
            color: 'white',
            fontWeight: 600,
            fontSize: '0.8rem',
            whiteSpace: 'nowrap',
          }}
        >
          {antrieb}
        </span>
      )}
      {energieWert != null && <Wert was={energieLabel} wert={energieWert} einheit="%" />}
    </div>
  )
}

/** Ein Feld im Bearbeitungsmodus — dieselbe Zeilenform wie die Anzeige. */
function Feld({
  was,
  wert,
  setzen,
  art = 'text',
  hinweis,
}: {
  was: string
  wert: string | number | null
  setzen: (w: string) => void
  art?: 'text' | 'number' | 'date'
  hinweis?: string
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '150px 1fr',
        gap: 12,
        padding: '5px 0',
        alignItems: 'center',
      }}
    >
      <label style={{ color: 'var(--grau-500)', fontSize: '0.85rem' }}>{was}</label>
      <div>
        <input
          type={art}
          value={wert ?? ''}
          onChange={(e) => setzen(e.target.value)}
          style={{ width: '100%' }}
        />
        {hinweis && (
          <small style={{ color: 'var(--grau-500)', fontSize: '0.75rem' }}>{hinweis}</small>
        )}
      </div>
    </div>
  )
}

export function FahrzeugAkte() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { hatRolle } = useAuth()
  const [akte, setAkte] = useState<Akte | null>(null)
  const [verlauf, setVerlauf] = useState<Zustand[]>([])
  const [laedt, setLaedt] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)
  const [zeigtVerlauf, setZeigtVerlauf] = useState(false)
  const [formular, setFormular] = useState<Akte | null>(null)
  const [speichert, setSpeichert] = useState(false)
  const [hinweis, setHinweis] = useState<string | null>(null)
  // Der letzte Zustand live aus der Backend-API — die Portaltabelle fahrzeugzustand hat
  // (noch) keinen Zufluss, deshalb kommt der echte aktuelle Stand von dort.
  const [live, setLive] = useState<AktuellerZustand | null>(null)

  const darfMelden = hatRolle('ADMIN', 'DISPONENT', 'WERKSTATT')
  // Ändern bleibt bei Verwaltung und Disposition — dieselbe Grenze wie in der API. Die
  // Werkstatt liest die Akte, sie pflegt sie nicht.
  const darfAendern = hatRolle('ADMIN', 'DISPONENT')

  async function speichern() {
    if (!formular || !id) return
    setSpeichert(true)
    setHinweis(null)
    try {
      // Ein PUT ersetzt den Datensatz vollständig. Deshalb geht der ganze Stand zurück,
      // nicht nur das Geänderte — sonst leerte ein Speichern die ausgelassenen Felder.
      await api.put(`/api/stammdaten/fahrzeuge/${id}`, {
        kennzeichen: formular.kennzeichen,
        fin: formular.fin,
        interneNummer: formular.interneNummer,
        fahrzeuggruppe: formular.fahrzeuggruppe,
        hersteller: formular.hersteller,
        typ: formular.typ,
        baujahr: formular.baujahr,
        erstzulassung: formular.erstzulassung,
        sitzplaetze: formular.sitzplaetze,
        stehplaetze: formular.stehplaetze,
        laengeMeter: formular.laengeMeter,
        antrieb: formular.antrieb,
        betriebshofId: formular.betriebshofId,
        huFaellig: formular.huFaellig,
        spFaellig: formular.spFaellig,
        serviceFaellig: formular.serviceFaellig,
        serviceKm: formular.serviceKm,
        telematikId: formular.telematikId,
        bemerkung: formular.bemerkung,
        aktiv: formular.aktiv,
      })
      setFormular(null)
      laden()
    } catch (ex) {
      setHinweis(ex instanceof Error ? ex.message : 'Speichern fehlgeschlagen')
    } finally {
      setSpeichert(false)
    }
  }

  const laden = useCallback(() => {
    if (!id) return
    setLaedt(true)
    api
      .get<Akte>(`/api/fahrzeuge/${id}`)
      .then(setAkte)
      .catch((ex) => setFehler(ex instanceof Error ? ex.message : 'Laden fehlgeschlagen'))
      .finally(() => setLaedt(false))
  }, [id])

  useEffect(laden, [laden])

  useEffect(() => {
    if (!zeigtVerlauf || !id) return
    api
      .get<Zustand[]>(`/api/fahrzeuge/${id}/verlauf`)
      .then(setVerlauf)
      .catch(() => setVerlauf([]))
  }, [zeigtVerlauf, id])

  // Live-Zustand nur holen, wenn eine Telematik-Kennung gesetzt ist. Scheitert der Abruf
  // (Backend-Ausfall) oder ist nichts eingerichtet, bleibt es bei null — die Box zeigt dann
  // den bisherigen Inhalt, keine Fehlermeldung auf der ganzen Akte.
  useEffect(() => {
    if (!id || !akte?.telematikId) {
      setLive(null)
      return
    }
    let abgebrochen = false
    letzterZustandHolen(Number(id))
      .then((z) => {
        if (!abgebrochen) setLive(z)
      })
      .catch(() => {
        if (!abgebrochen) setLive(null)
      })
    return () => {
      abgebrochen = true
    }
  }, [id, akte?.telematikId])

  if (laedt) return <div className="laden">Wird geladen …</div>
  if (fehler) return <div className="meldung fehler">{fehler}</div>
  if (!akte) return <div className="leer">Fahrzeug nicht gefunden.</div>

  // Live-first: Liegt ein aktueller Zustand aus der Backend-API vor, zeigt die Box ihn;
  // sonst den zuletzt in der Portaltabelle gespeicherten (Handeintrag) oder nichts.
  const liveZustand = live ? liveAlsZustand(live) : null
  const z = liveZustand ?? akte.zustand
  const istLive = liveZustand != null
  const hatPosition = z?.breite != null && z?.laenge != null
  // Im Live-Fall label-getrieben: alles, was das Gerät sendet (ohne Position, Fehler, Zeiten),
  // mit Beschriftung aus dem gemeinsamen Protokoll-Modul. So erscheinen auch Felder, die der
  // feste Zustand nicht kennt (Außentemperatur, Verbrauch, VIN …).
  const liveInhalt: Record<string, unknown> | null = liveZustand && live ? live.inhalt : null
  const liveFelder = liveInhalt ? telemetriefelder(liveInhalt, AUSSCHLUSS_BOX) : []
  // Antriebsart: die vom Gerät SELBST erkannte (im Zustand) hat Vorrang, sonst die im Stamm
  // gepflegte. Daraus entscheidet sich, ob die Karte die E-Bus- oder die Diesel-Ansicht zeigt.
  const istElektro = istElektroAntrieb(z?.antriebsart ?? akte.antrieb)
  const antriebAnzeige = antriebText(z?.antriebsart) ?? antriebText(akte.antrieb)

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <button
            type="button"
            className="knopf-schlicht"
            onClick={() => navigate('/fahrzeuge')}
            style={{ marginBottom: 8 }}
          >
            ← Fahrzeuge
          </button>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {akte.kennzeichen}
            {!akte.aktiv && (
              <span className="etikett grau">
                stillgelegt
              </span>
            )}
          </h1>
          <p>
            {[
              akte.interneNummer && `Wagen ${akte.interneNummer}`,
              akte.hersteller,
              akte.typ,
              akte.baujahr && `Baujahr ${akte.baujahr}`,
            ]
              .filter(Boolean)
              .join(' · ') || 'Keine weiteren Angaben'}
          </p>
        </div>
        {!formular && (
          <div style={{ display: 'flex', gap: 8 }}>
            {/* Führt zur Streckenkarte. Bewusst unabhängig vom zuletzt gemeldeten Zustand: Die
                Route kommt aus dem Rohspeicher über die Backend-API, nicht aus der
                fahrzeugzustand-Tabelle — ein Fahrzeug mit Telematik-Kennung kann eine Strecke
                haben, auch wenn hier noch nichts eingetragen ist. */}
            <button
              type="button"
              className="knopf-schlicht"
              onClick={() => navigate(`/fahrzeuge/${akte.id}/strecke`)}
            >
              Heute gefahren →
            </button>
            {darfAendern && (
              <button type="button" className="knopf-schlicht" onClick={() => setFormular(akte)}>
                Bearbeiten
              </button>
            )}
          </div>
        )}
      </div>

      {hinweis && <div className="meldung fehler">{hinweis}</div>}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(320px, 1fr) minmax(300px, 420px)',
          gap: 20,
          alignItems: 'start',
        }}
      >
        {/* ---------------------------------------------------------- Stammdaten */}
        <div className="karte" style={{ padding: 18 }}>
          <h2 style={{ fontSize: '0.95rem', marginBottom: 10 }}>Fahrzeugdaten</h2>

          {formular ? (
            <>
              <Feld
                was="Kennzeichen"
                wert={formular.kennzeichen}
                setzen={(w) => setFormular({ ...formular, kennzeichen: w })}
              />
              <Feld
                was="Fahrgestellnr."
                wert={formular.fin}
                setzen={(w) => setFormular({ ...formular, fin: w })}
                hinweis="17 Stellen, im Fahrzeugschein unter E"
              />
              <Feld
                was="Wagennummer"
                wert={formular.interneNummer}
                setzen={(w) => setFormular({ ...formular, interneNummer: w })}
              />
              <Feld
                was="Fahrzeuggruppe"
                wert={formular.fahrzeuggruppe}
                setzen={(w) => setFormular({ ...formular, fahrzeuggruppe: w })}
                hinweis="Spalte FzGr der Kursblätter"
              />
              <Feld
                was="Hersteller"
                wert={formular.hersteller}
                setzen={(w) => setFormular({ ...formular, hersteller: w })}
                hinweis="Entscheidet, welches Telematikportal in Frage kommt"
              />
              <Feld
                was="Typ"
                wert={formular.typ}
                setzen={(w) => setFormular({ ...formular, typ: w })}
              />
              <Feld
                was="Baujahr"
                art="number"
                wert={formular.baujahr}
                setzen={(w) => setFormular({ ...formular, baujahr: w ? Number(w) : null })}
              />
              <Feld
                was="Erstzulassung"
                art="date"
                wert={formular.erstzulassung}
                setzen={(w) => setFormular({ ...formular, erstzulassung: w || null })}
              />
              <Feld
                was="Sitzplätze"
                art="number"
                wert={formular.sitzplaetze}
                setzen={(w) => setFormular({ ...formular, sitzplaetze: w ? Number(w) : null })}
              />
              <Feld
                was="Stehplätze"
                art="number"
                wert={formular.stehplaetze}
                setzen={(w) => setFormular({ ...formular, stehplaetze: w ? Number(w) : null })}
              />
              <Feld
                was="Länge (m)"
                art="number"
                wert={formular.laengeMeter}
                setzen={(w) => setFormular({ ...formular, laengeMeter: w ? Number(w) : null })}
              />
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '150px 1fr',
                  gap: 12,
                  padding: '5px 0',
                  alignItems: 'center',
                }}
              >
                <label style={{ color: 'var(--grau-500)', fontSize: '0.85rem' }}>Antrieb</label>
                <div>
                  <select
                    value={formular.antrieb ?? ''}
                    onChange={(e) => setFormular({ ...formular, antrieb: e.target.value || null })}
                    style={{ width: '100%' }}
                  >
                    {ANTRIEB_WAHL.map((o) => (
                      <option key={o.wert} value={o.wert}>
                        {o.text}
                      </option>
                    ))}
                  </select>
                  <small style={{ color: 'var(--grau-500)', fontSize: '0.75rem' }}>
                    Ein FMS-Gerät erkennt den Antrieb selbst; dieser Eintrag greift, solange es
                    noch nichts gemeldet hat oder kein Gerät verbaut ist.
                  </small>
                </div>
              </div>

              <h2 style={{ fontSize: '0.95rem', margin: '18px 0 10px' }}>Fristen</h2>
              <Feld
                was="Hauptuntersuchung"
                art="date"
                wert={formular.huFaellig}
                setzen={(w) => setFormular({ ...formular, huFaellig: w || null })}
              />
              <Feld
                was="Sicherheitsprüfung"
                art="date"
                wert={formular.spFaellig}
                setzen={(w) => setFormular({ ...formular, spFaellig: w || null })}
              />
              <Feld
                was="Inspektion"
                art="date"
                wert={formular.serviceFaellig}
                setzen={(w) => setFormular({ ...formular, serviceFaellig: w || null })}
              />
              <Feld
                was="… oder bei km"
                art="number"
                wert={formular.serviceKm}
                setzen={(w) => setFormular({ ...formular, serviceKm: w ? Number(w) : null })}
                hinweis="Fällig ist, was zuerst eintritt"
              />

              <h2 style={{ fontSize: '0.95rem', margin: '18px 0 10px' }}>Sonstiges</h2>
              <Feld
                was="Telematik-Kennung"
                wert={formular.telematikId}
                setzen={(w) => setFormular({ ...formular, telematikId: w })}
                hinweis="Kennung des verbauten Geräts, sobald eines vorhanden ist"
              />
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '150px 1fr',
                  gap: 12,
                  padding: '5px 0',
                }}
              >
                <label style={{ color: 'var(--grau-500)', fontSize: '0.85rem' }}>Bemerkung</label>
                <textarea
                  rows={3}
                  value={formular.bemerkung ?? ''}
                  onChange={(e) => setFormular({ ...formular, bemerkung: e.target.value })}
                />
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button
                  type="button"
                  className="knopf-primaer"
                  onClick={speichern}
                  disabled={speichert || !formular.kennzeichen.trim()}
                >
                  {speichert ? 'Wird gespeichert …' : 'Speichern'}
                </button>
                <button
                  type="button"
                  className="knopf-schlicht"
                  onClick={() => {
                    setFormular(null)
                    setHinweis(null)
                  }}
                >
                  Abbrechen
                </button>
              </div>
            </>
          ) : (
            <>
          <Zeile was="Kennzeichen">
            <strong>{akte.kennzeichen}</strong>
          </Zeile>
          <Zeile was="Fahrgestellnr.">
            {akte.fin ? (
              <code style={{ fontSize: '0.85rem' }}>{akte.fin}</code>
            ) : (
              <span className="unbesetzt">nicht erfasst</span>
            )}
          </Zeile>
          <Zeile was="Wagennummer">{akte.interneNummer ?? '–'}</Zeile>
          <Zeile was="Fahrzeuggruppe">{akte.fahrzeuggruppe ?? '–'}</Zeile>
          <Zeile was="Hersteller / Typ">
            {[akte.hersteller, akte.typ].filter(Boolean).join(' ') || (
              <span className="unbesetzt">nicht erfasst</span>
            )}
          </Zeile>
          <Zeile was="Baujahr">{akte.baujahr ?? '–'}</Zeile>
          <Zeile was="Erstzulassung">{datum(akte.erstzulassung)}</Zeile>
          <Zeile was="Antrieb">{akte.antrieb ?? '–'}</Zeile>
          <Zeile was="Plätze">
            {akte.sitzplaetze != null || akte.stehplaetze != null
              ? `${akte.sitzplaetze ?? 0} Sitz- / ${akte.stehplaetze ?? 0} Stehplätze`
              : '–'}
          </Zeile>
          <Zeile was="Länge">{akte.laengeMeter ? `${akte.laengeMeter} m` : '–'}</Zeile>
          <Zeile was="Betriebshof">{akte.betriebshofName ?? '–'}</Zeile>

          <h2 style={{ fontSize: '0.95rem', margin: '18px 0 10px' }}>Fristen</h2>
          <Zeile was="Hauptuntersuchung">
            <Frist datum={akte.huFaellig} />
          </Zeile>
          <Zeile was="Sicherheitsprüfung">
            <Frist datum={akte.spFaellig} />
          </Zeile>
          <Zeile was="Inspektion">
            <Frist datum={akte.serviceFaellig} />
          </Zeile>
          <Zeile was="… oder bei km">
            {akte.serviceKm != null ? akte.serviceKm.toLocaleString('de-DE') : '–'}
          </Zeile>

          {(akte.bemerkung || akte.telematikId) && (
            <>
              <h2 style={{ fontSize: '0.95rem', margin: '18px 0 10px' }}>Sonstiges</h2>
              {akte.telematikId && <Zeile was="Telematik-Kennung">{akte.telematikId}</Zeile>}
              {akte.bemerkung && <Zeile was="Bemerkung">{akte.bemerkung}</Zeile>}
            </>
          )}
            </>
          )}
        </div>

        {/* ---------------------------------------------------------- Zustand */}
        <div className="karte" style={{ padding: 18 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 10,
            }}
          >
            <h2 style={{ fontSize: '0.95rem' }}>Zuletzt gemeldet</h2>
            {z && (
              <small style={{ color: 'var(--grau-500)' }}>
                {QUELLE_TEXT[z.quelle] ?? z.quelle}
              </small>
            )}
          </div>

          {!z ? (
            <div className="leer" style={{ padding: '18px 0' }}>
              <p style={{ marginBottom: 6 }}>Noch keine Meldung.</p>
              <small style={{ color: 'var(--grau-500)' }}>
                Position, Kilometerstand und Füllstand kommen, sobald ein Gerät verbaut oder
                ein Herstellerportal angebunden ist. Bis dahin lassen sie sich von Hand
                eintragen.
              </small>
            </div>
          ) : (
            <>
              <div style={{ color: 'var(--grau-500)', fontSize: '0.82rem', marginBottom: 12 }}>
                {zeitpunkt(z.gemessenAm)}
                {istLive && live && alterText(live) && (
                  <span style={{ color: live.aktuell === false ? 'var(--warnung, #b45309)' : undefined }}>
                    {' · '}
                    {alterText(live)}
                  </span>
                )}
              </div>

              <ZustandKopf
                istElektro={istElektro}
                antrieb={antriebAnzeige}
                batterieSoc={z.batterieSoc}
                ladestand={z.ladestand}
                fuellstand={z.fuellstand}
              />

              {liveInhalt ? (
                // Live aus der Backend-API: label-getrieben, zeigt jedes gesendete Feld.
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '150px 1fr',
                    gap: '2px 12px',
                    fontSize: '0.88rem',
                    marginBottom: 16,
                  }}
                >
                  {liveFelder.map((feld) => {
                    const def = FELD[feld]
                    const text = wertText(liveInhalt[feld])
                    return (
                      <Fragment key={feld}>
                        <span style={{ color: 'var(--grau-500)', padding: '3px 0' }}>
                          {def?.label ?? feld}
                        </span>
                        <span style={{ padding: '3px 0' }}>
                          {text}
                          {def?.einheit && text !== '—' ? ` ${def.einheit}` : ''}
                        </span>
                      </Fragment>
                    )
                  })}
                </div>
              ) : (
                // Handeintrag/Portaltabelle: das feste Gitter der bekannten Werte.
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
                    gap: 14,
                    marginBottom: 16,
                  }}
                >
                  <Wert was="Kilometerstand" wert={z.kmStand} einheit="km" />
                  <Wert was="Betriebsstunden" wert={z.betriebsstunden} einheit="h" />
                  {z.fuellstand != null && <Wert was="Kraftstoff" wert={z.fuellstand} einheit="%" />}
                  {z.ladestand != null && <Wert was="Ladung" wert={z.ladestand} einheit="%" />}
                  <Wert
                    was="Geschwindigkeit"
                    wert={z.geschwindigkeit}
                    einheit={z.geschwindigkeit != null ? 'km/h' : undefined}
                  />
                  <Wert
                    was="Zündung"
                    wert={z.zuendungAn == null ? null : z.zuendungAn ? 'an' : 'aus'}
                  />
                </div>
              )}

              {z.fehlertext && (
                <div className="meldung hinweis" style={{ marginBottom: 14 }}>
                  {z.fehlertext}
                </div>
              )}

              <h3 style={{ fontSize: '0.85rem', marginBottom: 8 }}>Standort</h3>
              {hatPosition ? (
                <>
                  <Karte
                    klein
                    hoehe={190}
                    punkte={[
                      {
                        breite: z.breite as number,
                        laenge: z.laenge as number,
                        richtung: z.geschwindigkeit && z.geschwindigkeit > 3 ? z.richtung : null,
                      },
                    ]}
                    beimKlicken={() =>
                      oeffneKarte({
                        breite: z.breite as number,
                        laenge: z.laenge as number,
                        titel: akte.kennzeichen,
                      })
                    }
                  />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginTop: 6,
                      fontSize: '0.78rem',
                      color: 'var(--grau-500)',
                    }}
                  >
                    <span>
                      {(z.breite as number).toFixed(5)}, {(z.laenge as number).toFixed(5)}
                    </span>
                    <button
                      type="button"
                      className="textknopf"
                      onClick={() =>
                        oeffneKarte({
                          breite: z.breite as number,
                          laenge: z.laenge as number,
                          titel: akte.kennzeichen,
                        })
                      }
                    >
                      Größer anzeigen
                    </button>
                  </div>
                </>
              ) : (
                <p className="unbesetzt" style={{ fontSize: '0.85rem' }}>
                  Keine Position in dieser Meldung.
                </p>
              )}

              <button
                type="button"
                className="knopf-schlicht"
                style={{ marginTop: 14 }}
                onClick={() => setZeigtVerlauf((v) => !v)}
              >
                {zeigtVerlauf ? 'Verlauf ausblenden' : 'Verlauf anzeigen'}
              </button>

              {zeigtVerlauf && (
                <div className="tabelle-huelle" style={{ marginTop: 10 }}>
                  {verlauf.length === 0 ? (
                    <div className="leer">Keine weiteren Meldungen.</div>
                  ) : (
                    <table>
                      <thead>
                        <tr>
                          <th>Zeitpunkt</th>
                          <th>km</th>
                          <th>Position</th>
                          <th>Quelle</th>
                        </tr>
                      </thead>
                      <tbody>
                        {verlauf.map((v) => (
                          <tr key={v.id}>
                            <td style={{ whiteSpace: 'nowrap' }}>{zeitpunkt(v.gemessenAm)}</td>
                            <td className="zahl">
                              {v.kmStand != null ? v.kmStand.toLocaleString('de-DE') : '–'}
                            </td>
                            <td>
                              {v.breite != null && v.laenge != null ? (
                                <button
                                  type="button"
                                  className="textknopf"
                                  onClick={() =>
                                    oeffneKarte({
                                      breite: v.breite as number,
                                      laenge: v.laenge as number,
                                      titel: `${akte.kennzeichen} · ${zeitpunkt(v.gemessenAm)}`,
                                    })
                                  }
                                >
                                  auf der Karte
                                </button>
                              ) : (
                                <span className="unbesetzt">–</span>
                              )}
                            </td>
                            <td>{QUELLE_TEXT[v.quelle] ?? v.quelle}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </>
          )}

          {darfMelden && (
            <p style={{ marginTop: 16, fontSize: '0.78rem', color: 'var(--grau-500)' }}>
              Kilometerstände lassen sich über die Schnittstelle eintragen, bis die
              Telematik angeschlossen ist. <Link to="/fahrzeuge">Zur Fahrzeugliste</Link>
            </p>
          )}
        </div>
      </div>
    </>
  )
}
